import { useMutation } from '@tanstack/react-query';
import { FirebaseError, initializeApp } from 'firebase/app';
import {
  connectAuthEmulator,
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from 'firebase/auth';

/** How the app signs a person in and proves who they are to the API. */
export type AuthProvider = {
  /** The bearer token for the next request; `undefined` while signed out. */
  getAccessToken: () => Promise<string | undefined>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Calls `listener` whenever the person signs in or out; returns the unsubscribe. */
  subscribe: (listener: (signedIn: boolean) => void) => () => void;
};

// Development builds sign every request with `VITE_DEV_TOKEN`; a production
// build never reads it, so it is signed out for good. Signing out only stops
// sending the token until the next sign-in.
function devAuth(): AuthProvider {
  const token = () =>
    import.meta.env.DEV
      ? import.meta.env.VITE_DEV_TOKEN || undefined
      : undefined;
  const listeners = new Set<(signedIn: boolean) => void>();
  let signedOut = false;
  const setSignedOut = (value: boolean) => {
    signedOut = value;
    for (const listener of listeners) {
      listener(!signedOut && token() !== undefined);
    }
  };

  return {
    getAccessToken: async () => (signedOut ? undefined : token()),
    signIn: async () => setSignedOut(false),
    signOut: async () => setSignedOut(true),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// The person closed Google's popup, or opened a second one over it: they chose
// not to sign in, which is not a failure to report.
const POPUP_DISMISSED = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
]);

// Google sign-in through Firebase Auth, or through its local emulator when
// `VITE_FIREBASE_AUTH_EMULATOR_HOST` is set.
function firebaseAuth(): AuthProvider {
  const env = import.meta.env;
  const auth = getAuth(
    initializeApp({
      apiKey: env.VITE_FIREBASE_API_KEY,
      authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: env.VITE_FIREBASE_PROJECT_ID,
      appId: env.VITE_FIREBASE_APP_ID,
    }),
  );
  if (env.VITE_FIREBASE_AUTH_EMULATOR_HOST) {
    // Without `disableWarnings` the SDK pins a banner over the bottom of the
    // page, where the sidebar's sign-out is.
    connectAuthEmulator(
      auth,
      `http://${env.VITE_FIREBASE_AUTH_EMULATOR_HOST}`,
      { disableWarnings: true },
    );
  }

  return {
    getAccessToken: async () => {
      // Before this resolves, a returning person still reads as signed out.
      await auth.authStateReady();
      return auth.currentUser?.getIdToken();
    },
    // A dismissed popup resolves signed out; the sign-in guard keeps the
    // visitor where they are.
    signIn: async () => {
      try {
        await signInWithPopup(auth, new GoogleAuthProvider());
      } catch (error) {
        if (
          !(error instanceof FirebaseError && POPUP_DISMISSED.has(error.code))
        ) {
          throw error;
        }
      }
    },
    signOut: () => signOut(auth),
    subscribe: (listener) =>
      onAuthStateChanged(auth, (user) => listener(user !== null)),
  };
}

function selectProvider(name = 'dev'): AuthProvider {
  switch (name) {
    case 'dev':
      return devAuth();
    case 'firebase':
      return firebaseAuth();
    default:
      throw new Error(
        `VITE_AUTH_PROVIDER must be dev or firebase, not ${name}`,
      );
  }
}

/** The app's one auth provider, picked at build time by `VITE_AUTH_PROVIDER`. */
export const auth = selectProvider(import.meta.env.VITE_AUTH_PROVIDER);

/** Signs in; a failure tells the user through the mutation cache. */
export function useSignIn({ onSuccess }: { onSuccess: () => unknown }) {
  return useMutation({ mutationFn: auth.signIn, onSuccess });
}
